import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

const STORAGE_URL = 'https://ymjeinhylrufskwhzobk.supabase.co/storage/v1/object/public/bible/rvr1960';

interface BookData {
  abbrev: string;
  name: string;
  chapters: string[][];
}

const BOOKS_MAP: Record<string, string> = {
  genesis: 'Génesis', exodo: 'Éxodo', levitico: 'Levítico', numeros: 'Números',
  deuteronomio: 'Deuteronomio', josue: 'Josué', jueces: 'Jueces', rut: 'Rut',
  '1-samuel': '1 Samuel', '2-samuel': '2 Samuel', '1-reyes:': '1 Reyes',
  '2-reyes': '2 Reyes', '1-cronicas': '1 Crónicas', '2-cronicas': '2 Crónicas',
  esdras: 'Esdras', nehemias: 'Nehemías', ester: 'Ester', job: 'Job',
  salmos: 'Salmos', proverbios: 'Proverbios', eclesiastes: 'Eclesiastés',
  cantares: 'Cantares', isaias: 'Isaías', jeremias: 'Jeremías',
  lamentaciones: 'Lamentaciones', ezequiel: 'Ezequiel', daniel: 'Daniel',
  oseas: 'Oseas', joel: 'Joel', amos: 'Amos', abdias: 'Abdías',
  jonas: 'Jonás', miqueas: 'Miqueas', nahum: 'Nahum', habacuc: 'Habacuc',
  sofonias: 'Sofonías', hageo: 'Hageo', zacarias: 'Zacarías', malaquias: 'Malaquías',
  mateo: 'Mateo', marcos: 'Marcos', lucas: 'Lucas', juan: 'Juan',
  hechos: 'Hechos', romanos: 'Romanos', '1-corintios': '1 Corintios',
  '2-corintios': '2 Corintios', galatas: 'Gálatas', efesios: 'Efesios',
  filipenses: 'Filipenses', colosenses: 'Colosenses', '1-tesalonicenses': '1 Tesalonicenses',
  '2-tesalonicenses': '2 Tesalonicenses', '1-timoteo': '1 Timoteo',
  '2-timoteo': '2 Timoteo', tito: 'Tito', filemon: 'Filemón',
  hebreos: 'Hebreos', santiago: 'Santiago', '1-pedro': '1 Pedro',
  '2-pedro': '2 Pedro', '1-juan': '1 Juan', '2-juan': '2 Juan',
  '3-juan': '3 Juan', judas: 'Judas', apocalipsis: 'Apocalipsis',
};

export default function BibleChapter() {
  const { book, chapter } = useParams<{ book: string; chapter: string }>();
  const [data, setData] = useState<BookData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentChapter = parseInt(chapter || '1');
  const bookName = BOOKS_MAP[book || ''] || book;

  useEffect(() => {
    const fetchChapter = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${STORAGE_URL}/${book}.json`);
        if (!response.ok) throw new Error('No se pudo cargar el capítulo');
        const json: BookData = await response.json();
        setData(json);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error desconocido');
      } finally {
        setLoading(false);
      }
    };
    if (book) fetchChapter();
  }, [book]);

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-8 bg-[#3A3A3C] rounded w-48 animate-pulse"></div>
        {[...Array(10)].map((_, i) => (
          <div key={i} className="h-4 bg-[#3A3A3C] rounded w-full animate-pulse"></div>
        ))}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">{error || 'Error al cargar'}</p>
        <Link to="/bible" className="text-primary mt-4 inline-block">← Volver</Link>
      </div>
    );
  }

  const chapters = data.chapters;
  const totalChapters = chapters.length;
  const verses = chapters[currentChapter - 1] || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Link to={`/bible/${book}`} className="text-primary">← Volver</Link>
        <h1 className="text-xl font-bold text-white">{bookName} {currentChapter}</h1>
        <div className="w-16"></div>
      </div>

      <div className="space-y-3">
        {verses.map((verseText, index) => (
          <Link
            key={index}
            to={`/bible/${book}/${currentChapter}/${index + 1}`}
            className="block card "
          >
            <span className="text-accent font-bold mr-2">{index + 1}</span>
            <span className="text-[#E5E5EA]">{verseText}</span>
          </Link>
        ))}
      </div>

      <div className="flex justify-between mt-6">
        {currentChapter > 1 ? (
          <Link to={`/bible/${book}/${currentChapter - 1}`} className="btn-outline">
            ← Anterior
          </Link>
        ) : (
          <div></div>
        )}
        {currentChapter < totalChapters && (
          <Link to={`/bible/${book}/${currentChapter + 1}`} className="btn-outline">
            Siguiente →
          </Link>
        )}
      </div>
    </div>
  );
}
